import { instance } from './instance';
import {
  ITenantResponse,
  TenantDomain,
  TenantDomainEmailConfig,
  TenantTheme,
  UpdateTenantPayload,
  UpdateTenantCurrencyParams,
  UpsertTenantDomainEmailConfigPayload,
  TenantFirebaseConfig,
  UpsertTenantFirebaseConfigPayload,
  TestTenantFirebaseConfigPayload,
  TestTenantFirebaseConfigResult,
} from '../types';

export const tenantApi = {
  getCurrencyPricing: async (): Promise<any> => {
    const response = await instance.get(`/currency-pricing`);
    return response.data;
  },
  getCurrencyByCode: async (code: string): Promise<any> => {
    const response = await instance.get(`/currency-pricing/code/${code}`);
    return response.data;
  },
  getTenantBySlug: async (slug: string): Promise<ITenantResponse> => {
    const response = await instance.get(`/tenant/slug/${slug}`);
    return response.data;
  },

  getTenantByDomain: async (domain: string): Promise<ITenantResponse> => {
    const response = await instance.get(`/tenant/domain/${domain}`);
    return { data: response.data.data.tenant };
  },

  updateTenant: async (
    id: string,
    payload: UpdateTenantPayload
  ): Promise<ITenantResponse> => {
    const response = await instance.patch(`/tenant/${id}`, payload);
    return response.data;
  },

  getTenantTheme: async (): Promise<TenantTheme> => {
    const response = await instance.get(`/tenant/public/theme`);
    return response.data.data;
  },

  // Admin-only theme fetch — includes internal fields (e.g. imageStylePrompt) not
  // exposed on the public theme endpoint. Used by the settings screen.
  getTenantThemeAdmin: async (id: string): Promise<TenantTheme> => {
    const response = await instance.get(`/tenant/${id}/theme`);
    return response.data.data;
  },

  updateTenantTheme: async (
    id: string,
    payload: Partial<TenantTheme>
  ): Promise<TenantTheme> => {
    const response = await instance.put(`/tenant/${id}/theme`, payload);
    return response.data;
  },

  updateTenantCurrency: async (
    currencyId: string | number,
    payload: UpdateTenantCurrencyParams
  ): Promise<any> => {
    const response = await instance.put(
      `/currency-pricing/tenant/${currencyId}`,
      payload
    );
    return response.data;
  },

  addSupportingCurrencies: async (payload: {
    currencyIds: string[];
    isBase: boolean;
    isSupported: boolean;
  }): Promise<any> => {
    const response = await instance.post(
      `/currency-pricing/tenant/add`,
      payload
    );
    return response.data;
  },

  getSupportedCurrencies: async (): Promise<{
    data: Array<{
      id: string;
      code: string;
      name: string;
      symbol: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
  }> => {
    const response = await instance.get('/currency-pricing/tenant/supported');
    return response.data;
  },

  getBaseCurrency: async (): Promise<{
    data: {
      id: string;
      code: string;
      name: string;
      symbol: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    };
  }> => {
    const response = await instance.get('/currency-pricing/tenant/base');
    return response.data;
  },

  removeSupportingCurrency: async (currencyId: string): Promise<any> => {
    const response = await instance.delete(
      `currency-pricing/tenant/${currencyId}`
    );
    return response.data;
  },

  getTenantDomains: async (tenantId: string): Promise<TenantDomain[]> => {
    const response = await instance.get(`/tenant/${tenantId}/domains`);
    const payload = response.data?.data ?? response.data;
    return Array.isArray(payload) ? payload : [];
  },

  getTenantDomainEmailConfig: async (
    tenantId: string,
    domainId: string
  ): Promise<TenantDomainEmailConfig> => {
    const response = await instance.get(
      `/tenant/${tenantId}/domains/${domainId}/email-config`
    );
    return response.data;
  },

  upsertTenantDomainEmailConfig: async (
    tenantId: string,
    domainId: string,
    payload: UpsertTenantDomainEmailConfigPayload
  ): Promise<TenantDomainEmailConfig> => {
    const response = await instance.put(
      `/tenant/${tenantId}/domains/${domainId}/email-config`,
      payload
    );
    return response.data;
  },

  getTenantFirebaseConfig: async (
    tenantId: string
  ): Promise<TenantFirebaseConfig> => {
    const response = await instance.get(`/tenant/${tenantId}/push-configs/USER`);
    return response.data?.data ?? response.data;
  },

  upsertTenantFirebaseConfig: async (
    tenantId: string,
    payload: UpsertTenantFirebaseConfigPayload
  ): Promise<TenantFirebaseConfig> => {
    const response = await instance.put(`/tenant/${tenantId}/push-configs/USER`, payload);
    return response.data?.data ?? response.data;
  },

  testTenantFirebaseConfig: async (
    tenantId: string,
    payload: TestTenantFirebaseConfigPayload
  ): Promise<TestTenantFirebaseConfigResult> => {
    const response = await instance.post(`/tenant/${tenantId}/push-configs/USER/test`, payload);
    return response.data?.data ?? response.data;
  },
};
